import './style.modules.scss'

import { AnalyticsTagBuilder } from '@dai-team/analytics-library'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import xss from 'xss'

import { Promo, PromoProps } from '../Promo/Promo'

export type HorizontalTabsProps = {
  labels: {
    child: { title: string }[]
  }
  panels: HorizontalPanelProps[]
  className?: string
  testId?: string
}

export type HorizontalPanelProps = PromoProps & {
  footer?: string
}

/** @deprecated Use HorizontalPanelProps instead */
export type PanelProps = HorizontalPanelProps

type HorizontalTabProps = {
  isActive: boolean
  label: string
  index: number
  onClick: (index: number) => void
  onKeyDown: (event: React.KeyboardEvent, index: number) => void
  panelId: string
  testId?: string
  tabHeadingRef: (el: HTMLButtonElement) => void
}

type HorizontalTabPanelProps = {
  isActive: boolean
  panel: HorizontalPanelProps
  tabId: string
  testId?: string
  panelRef: (el: HTMLDivElement | null) => void
  onPanelKeyDown: (event: React.KeyboardEvent) => void
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), iframe'

/**
 * Returns visible focusable elements within a container, excluding
 * elements hidden by Video.js or browser hidden states.
 */
const getVisibleFocusables = (
  container: HTMLElement,
  ignoreHiddenAncestor = false
): HTMLElement[] => {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
  ).filter(el => {
    // Exclude elements inside vjs-hidden containers
    if (el.closest('.vjs-hidden')) return false
    // Exclude elements with display:none
    if (el.closest('[style*="display: none"]')) return false
    // Exclude elements inside hidden ancestors (but skip the container itself)
    if (!ignoreHiddenAncestor) {
      const hiddenAncestor = el.closest('[hidden]')
      if (hiddenAncestor && hiddenAncestor !== container) return false
    }
    // Exclude the seek-to-live control
    if (el.classList.contains('vjs-seek-to-live-control')) return false
    return true
  })
}

const HorizontalTab = ({
  isActive,
  label,
  index,
  onClick,
  onKeyDown,
  panelId,
  testId,
  tabHeadingRef
}: HorizontalTabProps) => {
  const atb = new AnalyticsTagBuilder().setDefaultName('SelectionTab')

  return (
    <button
      ref={tabHeadingRef}
      className={`HorizontalTabs-tabItem u-textCenter ${
        isActive ? 'is-active' : ''
      }`}
      onClick={() => onClick(index)}
      onKeyDown={event => onKeyDown(event, index)}
      tabIndex={isActive ? 0 : -1}
      role="tab"
      aria-selected={isActive}
      aria-controls={panelId}
      id={`tab-${index + 1}`}
      data-testid={testId ? `${testId}-${index + 1}` : undefined}
      {...atb.dataAnalyticAttribute(String(index + 1))}
    >
      <div
        className={`HorizontalTabs-tabLabel u-paddingVert32 u-textSizeMsrp1 ${
          isActive ? 'u-textColorBerry u-textBold' : 'u-textColorActionBlue'
        }`}
        dangerouslySetInnerHTML={{ __html: xss(label) }}
      />
    </button>
  )
}

const HorizontalTabPanel = ({
  isActive,
  panel,
  tabId,
  testId,
  panelRef,
  onPanelKeyDown
}: HorizontalTabPanelProps) => {
  return (
    <div
      className={`HorizontalTabs-panel u-sizeFull ${
        !isActive ? 'u-md-hidden u-lg-hidden u-sm-hidden' : ''
      }`}
      ref={panelRef}
      role="tabpanel"
      aria-labelledby={tabId}
      hidden={!isActive}
      data-testid={testId ? `${testId}` : undefined}
      onKeyDown={onPanelKeyDown}
    >
      <Promo {...panel} />
      {panel.footer && (
        <footer>
          <div
            className="Content u-paddingTop20 u-textCenter u-textSizeMsr u-sizeConstrained"
            dangerouslySetInnerHTML={{ __html: xss(panel.footer) }}
          />
        </footer>
      )}
    </div>
  )
}

export const HorizontalTabs = ({
  labels: { child: tabs },
  panels,
  className,
  testId
}: HorizontalTabsProps) => {
  const tabHeadingsRef = useRef<(HTMLButtonElement | null)[]>([])
  const mountedRef = useRef(false)
  const panelRefs = useRef<(HTMLDivElement | null)[]>([])
  const [activeIndex, setActiveIndex] = useState(0)
  const skipFocusRef = useRef(false)
  const pendingFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true
      return
    }
    if (skipFocusRef.current) {
      skipFocusRef.current = false
      if (pendingFocusRef.current) {
        pendingFocusRef.current.focus()
        pendingFocusRef.current = null
      }
      return
    }
    if (tabHeadingsRef.current[activeIndex]) {
      tabHeadingsRef.current[activeIndex]?.focus()
      tabHeadingsRef.current[activeIndex]?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      })
    }
  }, [activeIndex])

  const setPanelRef = useCallback(
    (index: number) => (el: HTMLDivElement | null) => {
      panelRefs.current[index] = el
    },
    []
  )

  const setTabHeadingRef = useCallback(
    (index: number) => (el: HTMLButtonElement | null) => {
      tabHeadingsRef.current[index] = el
    },
    []
  )

  const tabCount = tabs.length

  const handleTabClick = (index: number) => {
    setActiveIndex(index)
  }

  const handleTabKeyDown = (event: React.KeyboardEvent, index: number) => {
    const { key, shiftKey } = event
    const navigationKeys: Record<string, number> = {
      ArrowLeft: (activeIndex - 1 + tabCount) % tabCount,
      ArrowRight: (activeIndex + 1) % tabCount,
      Home: 0,
      End: tabCount - 1
    }
    if (key in navigationKeys) {
      event.preventDefault()
      setActiveIndex(navigationKeys[key])
      return
    }

    // Tab from a tab heading moves focus into the panel's first focusable
    // If the panel has no focusable content, move to the next tab heading
    if (key === 'Tab' && !shiftKey) {
      const panel = panelRefs.current[index]
      if (panel) {
        const focusables = getVisibleFocusables(panel, true)
        if (focusables.length > 0) {
          event.preventDefault()
          if (index === activeIndex) {
            focusables[0].focus()
          } else {
            skipFocusRef.current = true
            pendingFocusRef.current = focusables[0]
            setActiveIndex(index)
          }
        } else if (index + 1 < tabCount) {
          // No focusable content in panel, move to next tab
          event.preventDefault()
          setActiveIndex(index + 1)
        }
      }
    }

    // Shift+Tab from a tab heading moves focus to previous panel's last focusable
    // If the previous panel has no focusable content, move to the previous tab heading
    if (key === 'Tab' && shiftKey && index > 0) {
      const prevPanel = panelRefs.current[index - 1]
      if (prevPanel) {
        const focusables = getVisibleFocusables(prevPanel, true)
        if (focusables.length > 0) {
          event.preventDefault()
          if (index - 1 === activeIndex) {
            focusables[focusables.length - 1].focus()
          } else {
            skipFocusRef.current = true
            pendingFocusRef.current = focusables[focusables.length - 1]
            setActiveIndex(index - 1)
          }
        } else {
          // No focusable content in previous panel, move to previous tab heading
          event.preventDefault()
          setActiveIndex(index - 1)
        }
      }
    }
  }

  /**
   * Handles keydown within a panel. Intercepts:
   * - Tab on the last focusable element → move focus to next tab heading
   * - Shift+Tab on the first focusable element → move focus back to this panel's tab heading
   * All other keyboard interaction is left to the browser.
   */
  const handlePanelKeyDown =
    (panelIndex: number) => (event: React.KeyboardEvent) => {
      if (event.key !== 'Tab') return

      const panel = panelRefs.current[panelIndex]
      if (!panel) return

      const focusables = getVisibleFocusables(panel)
      if (focusables.length === 0) return

      const firstFocusable = focusables[0]
      const lastFocusable = focusables[focusables.length - 1]

      // Shift+Tab on first focusable → back to this panel's tab heading
      if (event.shiftKey && document.activeElement === firstFocusable) {
        event.preventDefault()
        tabHeadingsRef.current[panelIndex]?.focus()
        return
      }

      // Tab on last focusable → forward to next tab heading
      if (!event.shiftKey && document.activeElement === lastFocusable) {
        const nextIndex = panelIndex + 1
        if (nextIndex < tabCount) {
          event.preventDefault()
          setActiveIndex(nextIndex)
        }
      }
    }

  const handleMobileNavigation = (direction: 'previous' | 'next') => {
    direction === 'previous'
      ? setActiveIndex((activeIndex - 1 + tabCount) % tabCount)
      : setActiveIndex((activeIndex + 1) % tabCount)
  }

  return (
    <div
      className={className ? `HorizontalTabs ${className}` : 'HorizontalTabs'}
      data-testid={testId ? `${testId}` : undefined}
    >
      {tabs?.length > 1 && (
        <div
          className={`HorizontalTabs-tabsContainer u-flex u-flexRow u-flexJustifyCenter u-sm-flexJustifyStart`}
        >
          <div className="u-flex u-paddingHoriz0" role="tablist">
            {tabs.map((tab, idx) => (
              <HorizontalTab
                key={idx}
                isActive={activeIndex === idx}
                index={idx}
                label={tab.title}
                onClick={handleTabClick}
                onKeyDown={handleTabKeyDown}
                panelId={`panel-${idx}`}
                testId={testId ? `${testId}` : undefined}
                tabHeadingRef={setTabHeadingRef(idx)}
              />
            ))}
          </div>
        </div>
      )}
      <div className="HorizontalTabs-panels u-flex u-flexRow u-flexJustifyCenter u-sm-flexJustifyStart">
        {panels.map((panel: HorizontalPanelProps, idx: number) => (
          <HorizontalTabPanel
            key={idx}
            panel={panel}
            isActive={activeIndex === idx}
            tabId={`tab-${idx + 1}`}
            testId={testId ? `${testId}-panel-${idx + 1}` : undefined}
            panelRef={setPanelRef(idx)}
            onPanelKeyDown={handlePanelKeyDown(idx)}
          />
        ))}
      </div>

      {tabs.length > 1 && (
        <div className="u-sizeConstrained u-flex u-flexAlignItemsEnd u-lg-hidden u-md-hidden u-paddingVert20">
          <button
            id="carousel-previous"
            aria-hidden="false"
            className="Button Button--secondary Button--secondary--action-blue Button--icon Button--icon--stroke u-flex u-padding0 u-marginRight16"
            onClick={() => handleMobileNavigation('previous')}
            disabled={activeIndex === 0}
          >
            <span
              className="adel-material-symbols-24 u-textColorActionBlue"
              role="img"
              title="Slide Left"
              aria-label="Slide Left"
            >
              chevron_left
            </span>
          </button>
          <button
            id="carousel-next"
            aria-hidden="false"
            className="Button Button--secondary Button--secondary--action-blue Button--icon Button--icon--stroke u-flex u-padding0 u-marginRight16"
            onClick={() => handleMobileNavigation('next')}
            disabled={activeIndex === tabs?.length - 1}
          >
            <span
              className="adel-material-symbols-24 u-textColorActionBlue"
              role="img"
              title="Slide Right"
              aria-label="Slide Right"
            >
              chevron_right
            </span>
          </button>
          <p className="u-textColorActionBlue u-paddingLeft0">
            {activeIndex + 1} / {tabs.length}
          </p>
        </div>
      )}
    </div>
  )
}
